/**
 * The queue's rows: who sent what, when, and the two controls on each one.
 *
 * ── Three targets per row, and none of them nested ───────────────────────────────────────────
 * The row's own button carries the person, the form and the preview and OPENS the submission —
 * since 17 September 2026 in the report dialog rather than in a pane beside the list; the eye does
 * the same thing with an icon, because the canon draws one and a trainer reaching for it should not
 * have to know the whole row is pressable; the check marks it read. Nested interactive elements are
 * invalid, so the two controls are SIBLINGS of the row button rather than inside it — which also
 * puts them in the tab order in the order they are drawn.
 *
 * ── The check moves focus before it removes itself ───────────────────────────────────────────
 * A reviewed row leaves the list, so the tick the trainer pressed is about to be destroyed. The
 * callback runs before the refetch: focus goes to the row button of the submission that will take
 * this one's place, and the live region says what happened. Without it focus falls to `<body>`.
 */

import { useTranslation } from 'react-i18next';

import { AvatarChip } from '~/shared/ui/AvatarChip';
import { CheckIcon, EyeIcon } from '~/shared/ui/AuthIcons';
import iconButton from '~/shared/ui/iconButton.module.css';

import type { ReviewRow } from '../model/reviewQueue';
import styles from './ReviewQueue.module.css';

/** The id a row's own button carries, so the mark can hand focus to the row that follows it. */
export function reviewRowId(assignmentId: number): string {
  return `review-row-${String(assignmentId)}`;
}

interface ReviewQueueListProps {
  readonly rows: readonly ReviewRow[];
  /** The submission the report dialog is open on, or `null`. Nothing is „current" while it is shut. */
  readonly openedId: number | null;
  readonly onOpen: (assignmentId: number) => void;
  readonly onMark: (row: ReviewRow) => void;
  readonly marking: boolean;
  readonly reveal: string;
}

export function ReviewQueueList({
  rows,
  openedId,
  onOpen,
  onMark,
  marking,
  reveal,
}: ReviewQueueListProps) {
  const { t } = useTranslation();

  return (
    <ul className={`${String(styles.list)} ${reveal}`}>
      {rows.map((row) => (
        <li
          key={row.assignmentId}
          className={`${String(styles.row)} ${
            row.assignmentId === openedId ? String(styles.rowOn) : ''
          }`}
        >
          <AvatarChip initials={row.initials} avatarUrl={row.avatarUrl} loading="lazy" />

          <button
            type="button"
            id={reviewRowId(row.assignmentId)}
            className={styles.rowButton}
            aria-current={row.assignmentId === openedId}
            onClick={() => {
              onOpen(row.assignmentId);
            }}
          >
            <span className={styles.rowTop}>
              <span className={styles.rowName}>{row.name}</span>
              <span className={styles.rowForm}>{row.templateTitle}</span>
            </span>
            <span className={styles.rowSummary}>{row.summary}</span>
          </button>

          <span
            className={`${String(styles.rowWhen)} ${
              row.late ? String(styles.rowWhenLate) : ''
            }`}
          >
            {/* The pip is the canon's second channel on an on-time submission, never the only one:
              * lateness is said in words inside `row.when`. */}
            {row.late ? null : <i className={styles.pip} aria-hidden />}
            {row.when}
          </span>

          <button
            type="button"
            className={`${String(iconButton.iconButton)} ${String(styles.rowControl)}`}
            aria-label={t('web:reports.queue.open', {
              client: row.name,
              survey: row.templateTitle,
            })}
            onClick={() => {
              onOpen(row.assignmentId);
            }}
          >
            <EyeIcon className={styles.rowControlIcon} />
          </button>

          <button
            type="button"
            className={`${String(iconButton.iconButton)} ${String(styles.rowControl)}`}
            aria-label={t('web:reports.queue.mark', {
              client: row.name,
              survey: row.templateTitle,
            })}
            // `aria-disabled` rather than `disabled`: a control carrying a reason has to stay in the
            // tab order for the reason to be reachable, and `iconButton` styles both.
            aria-disabled={marking}
            onClick={() => {
              if (!marking) onMark(row);
            }}
          >
            <CheckIcon className={styles.rowControlIcon} />
          </button>
        </li>
      ))}
    </ul>
  );
}
